import { Discount } from '../../types/DiscountTypes'
import { fixMathRounding, getPriceFromCalculation, getBasePriceFromCalculation } from './CalculateDiscountHelpers'
import {
  CalculateDiscountLogic,
  Calculation,
  CalculationDiscount,
  CalculationUserInput,
  DiscountType
} from './CalculateDiscountTypes'

export class MemberDiscountLogic implements CalculateDiscountLogic {
  type: DiscountType = 'member'
  discountDefinition: Discount

  constructor(discount: Discount) {
    this.discountDefinition = discount
  }

  chooseEligible(previousCalculations: Calculation[], userInput: CalculationUserInput) {
    if (!userInput.userActivePeriods) return []

    return (
      previousCalculations
        // get only these activities that are in user's active periods
        .filter((calculation) => {
          // Use periodId if available, fallback to legacy period field
          const activityPeriod = calculation.activity.periodId || calculation.activity.period
          const userActivePeriods = userInput.userActivePeriods || []

          // Handle special case for 'anytime' membership
          if (userActivePeriods.includes('ANYTIME_MEMBERSHIP')) {
            return true
          }

          // Check for exact period match (same_period)
          if (userActivePeriods.includes(activityPeriod)) {
            return true
          }

          // Check for year match (same_year) — resolve via Period.periodStart when periodId is set,
          // so the year is accurate even after the period identifier is an opaque ID.
          const resolvedYear = (() => {
            const periodId = calculation.activity.periodId
            if (periodId && userInput.clientPeriods) {
              const period = userInput.clientPeriods.find((p) => p.id === periodId)
              if (period?.periodStart) return new Date(period.periodStart).getFullYear().toString()
            }
            // Legacy fallback: extract 4-digit year from strings like "HT 2024"
            const match = activityPeriod.match(/\d{4}/)
            return match ? match[0] : activityPeriod.substring(0, 4)
          })()
          if (userActivePeriods.includes(resolvedYear)) {
            return true
          }

          return false
        })
        // get only these activities that have this discount
        .map((calculation) =>
          calculation.activity.discountIds.includes(this.discountDefinition.id) ? calculation.activity.id : null
        )
        // cleanup: remove nulls
        .filter((activityId): activityId is string => activityId !== null)
    )
  }

  calculate(previousCalculations: Calculation[], userInput: CalculationUserInput) {
    const eligibleActivities = this.chooseEligible(previousCalculations, userInput)

    previousCalculations.map((calculation) => {
      if (!eligibleActivities.includes(calculation.activity.id)) return
      const activityPrice = getPriceFromCalculation(calculation)
      const basePrice = getBasePriceFromCalculation(calculation)
      const discountAmount =
        this.discountDefinition.calculationType === 'amount'
          ? this.discountDefinition.value // amount discount
          : fixMathRounding(basePrice * (this.discountDefinition.value / 100)) // percentage discount on base price
      const memberDiscount: CalculationDiscount = {
        type: this.type,
        name: this.discountDefinition.name,
        id: this.discountDefinition.id,
        input: activityPrice,
        discount: discountAmount,
        output: fixMathRounding(activityPrice - discountAmount),
        discountCalculationType: this.discountDefinition.calculationType,
        discountValue: this.discountDefinition.value,
        discountGroup: this.discountDefinition.discountGroup
      }
      calculation.discounts = calculation.discounts || []
      calculation.discounts.push(memberDiscount)
      calculation.finalPrice = memberDiscount.output
    })

    return previousCalculations
  }
}
